import { ReactNode, useEffect, useState } from 'react';
import { MsalProvider, useIsAuthenticated } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import { AuthProvider } from '@/contexts/AuthContext';
import { msalConfig } from '@/config/authConfig';
import { LoginPage } from './LoginPage';

interface AuthWrapperProps {
  children: ReactNode;
}

export function AuthWrapper({ children }: AuthWrapperProps) {
  const [msalInstance, setMsalInstance] = useState<PublicClientApplication | null>(null);
  const [isInitialised, setIsInitialised] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);

  useEffect(() => {
    const initMsal = async () => {
      // Check if Microsoft Entra ID is configured
      if (!import.meta.env.VITE_AZURE_CLIENT_ID) {
        console.error('Microsoft Entra ID not configured - VITE_AZURE_CLIENT_ID is required');
        setInitError('Authentication not configured. Please contact an administrator.');
        setIsInitialised(true);
        return;
      }

      try {
        const instance = new PublicClientApplication(msalConfig);
        await instance.initialize();
        setMsalInstance(instance);
        setIsInitialised(true);

        if (import.meta.env.DEV) {
          console.log('Microsoft Entra ID authentication enabled');
        }
      } catch (error) {
        console.error('Failed to initialise Microsoft Entra ID:', error);
        setInitError('Failed to initialise authentication. Please refresh the page.');
        setIsInitialised(true);
      }
    };

    initMsal();
  }, []);

  // Show loading state while initialising
  if (!isInitialised) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted">Initialising...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (initError || !msalInstance) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md p-8">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <svg
              className="w-8 h-8 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold mb-2">Authentication Error</h2>
          <p className="text-muted">
            {initError || 'Failed to initialise authentication.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <MsalProvider instance={msalInstance}>
      <AuthProvider>
        <ProtectedRoute>{children}</ProtectedRoute>
      </AuthProvider>
    </MsalProvider>
  );
}

function ProtectedRoute({ children }: { children: ReactNode }) {
  const isAuthenticated = useIsAuthenticated();

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  return <>{children}</>;
}
